import React from 'react';

const SKIP_FAIL_REASONS = new Set(['No data', 'Invalid times', 'No Boarding', 'No Ramp Task']);

const overturnLabel = (row) => {
    if (row?.reasonCode === 'other') return 'Other';
    const text = row?.passReason || 'Override';
    if (text.startsWith('Other – ') || text.startsWith('Other - ')) return 'Other';
    return text;
};

const tally = (labelsByBooking) => {
    const counts = new Map();
    labelsByBooking.forEach((label) => {
        counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [...counts.entries()]
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

// Same rules as prmassist-backend/prm-cpu/utils/sla-reason-summary.js.
// Used when the report still has passenger rows and the API did not attach a summary.
export const summarizeSlaReasons = (rows) => {
    const failByBooking = new Map();
    const overturnByBooking = new Map();

    (rows || []).forEach((row) => {
        const id = row?.bookingId;
        if (!id) return;
        if ((row.isManualOverride || row.isIgnored) && !overturnByBooking.has(id)) {
            overturnByBooking.set(id, overturnLabel(row));
        }
    });

    (rows || []).forEach((row) => {
        const id = row?.bookingId;
        if (!id || overturnByBooking.has(id) || row.isManualOverride || row.isIgnored) return;
        if (row.pass === false && !SKIP_FAIL_REASONS.has(row.passReason) && !failByBooking.has(id)) {
            failByBooking.set(id, row.passReason || 'Unknown');
        }
    });

    return {
        failReasons: tally(failByBooking),
        overturnReasons: tally(overturnByBooking),
    };
};

const rowsFromDailyReports = (dailyReports) => {
    const rows = [];
    (dailyReports || []).forEach((report) => {
        const kpis = report?.kpis;
        if (!kpis) return;
        Object.keys(kpis).forEach((key) => {
            const rawData = kpis[key]?.rawData;
            if (Array.isArray(rawData)) rows.push(...rawData);
        });
    });
    return rows;
};

export const reasonSummaryFromReports = (reasonSummary, dailyReports) => {
    if (reasonSummary) return reasonSummary;
    const rows = rowsFromDailyReports(dailyReports);
    if (!rows.length) return null;
    return summarizeSlaReasons(rows);
};

export const mergeReasonSummaries = (summaries) => {
    const add = (key) => {
        const counts = new Map();
        (summaries || []).forEach((summary) => {
            (summary?.[key] || []).forEach(({ label, count }) => {
                if (!label) return;
                counts.set(label, (counts.get(label) || 0) + (count || 0));
            });
        });
        return [...counts.entries()]
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    };

    return {
        failReasons: add('failReasons'),
        overturnReasons: add('overturnReasons'),
    };
};

const listStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 12px',
    fontSize: '13px',
    color: '#1f2937',
    textAlign: 'left',
};

const ReasonTable = ({ title, rows }) => {
    const top = (rows || []).slice(0, 5);

    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>{title}</p>
            {top.length === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>None in this report.</p>
            ) : (
                <table style={listStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...cellStyle, backgroundColor: '#f9fafb', fontWeight: 600 }}>Reason</th>
                            <th style={{ ...cellStyle, backgroundColor: '#f9fafb', fontWeight: 600, width: '80px', textAlign: 'right' }}>Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {top.map((row) => (
                            <tr key={row.label}>
                                <td style={cellStyle}>{row.label}</td>
                                <td style={{ ...cellStyle, textAlign: 'right' }}>{row.count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const SlaReasonSummaryPanel = ({ summary, heading }) => {
    if (!summary) return null;

    return (
        <div style={{ marginTop: '16px', marginBottom: '8px' }}>
            {heading && (
                <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{heading}</p>
            )}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <ReasonTable title="Top 5 SLA fail reasons" rows={summary.failReasons} />
                <ReasonTable title="Top 5 override reasons" rows={summary.overturnReasons} />
            </div>
        </div>
    );
};

export default SlaReasonSummaryPanel;
